import { useMemo, useState } from "react";
import type { ValveType } from "../calc/gas";
import {
  sizeLiquid,
  type LiquidSizingInput,
  type Viscosity,
} from "../calc/liquid";
import { selectOrificeWithParallel } from "../calc/orifice";
import { Field, Item, VALVE_LABELS, num } from "./formBits";

type Certification = "certified" | "non-certified";
type ViscosityKind = "none" | "cP" | "SSU";

interface FormState {
  certification: Certification;
  flow: string;
  specificGravity: string;
  setPressure: string;
  overpressure: string;
  backpressure: string;
  valveType: ValveType;
  viscosityKind: ViscosityKind;
  viscosity: string;
  kw: string;
  kc: string;
  kp: string;
}

/** 초기값은 API 520 Part I 5.8.2 Example 5 조건이다. 결과는 P 오리피스. */
const INITIAL: FormState = {
  certification: "certified",
  flow: "6814",
  specificGravity: "0.9",
  setPressure: "1724", // 250 psig
  overpressure: "10",
  backpressure: "345", // 50 psig
  valveType: "balanced-bellows",
  viscosityKind: "SSU",
  viscosity: "2000",
  kw: "0.97", // Figure 32
  kc: "1.0",
  kp: "",
};

export function LiquidSizingPanel() {
  const [f, setF] = useState<FormState>(INITIAL);

  const set =
    <K extends keyof FormState>(key: K) =>
    (value: FormState[K]) =>
      setF((prev) => ({ ...prev, [key]: value }));

  /** 액체 식은 게이지압을 쓴다. P1 = Ps × (1 + 과압) */
  const p1Gauge = useMemo(
    () => num(f.setPressure) * (1 + num(f.overpressure) / 100),
    [f.setPressure, f.overpressure],
  );

  const sizing = useMemo(() => {
    const viscosity: Viscosity | undefined =
      f.viscosityKind === "none"
        ? undefined
        : { kind: f.viscosityKind, value: num(f.viscosity) };
    const kw = f.kw.trim() === "" ? undefined : num(f.kw);
    const kp = f.kp.trim() === "" ? undefined : num(f.kp);

    const common = {
      requiredFlowLpm: num(f.flow),
      specificGravity: num(f.specificGravity),
      backpressureKpaGauge: num(f.backpressure),
      valveType: f.valveType,
      combinationCorrection: num(f.kc),
      ...(viscosity === undefined ? {} : { viscosity }),
      ...(kw === undefined ? {} : { bellowsBackpressureCorrection: kw }),
    };

    const input: LiquidSizingInput =
      f.certification === "certified"
        ? { ...common, certification: "certified", relievingPressureKpaGauge: p1Gauge }
        : {
            ...common,
            certification: "non-certified",
            setPressureKpaGauge: num(f.setPressure),
            ...(kp === undefined ? {} : { overpressureCorrection: kp }),
          };

    return sizeLiquid(input);
  }, [f, p1Gauge]);

  const selection = useMemo(
    () => (sizing.ok ? selectOrificeWithParallel(sizing.requiredAreaMm2) : null),
    [sizing],
  );

  const isBellows = f.valveType === "balanced-bellows";
  const isNonCertified = f.certification === "non-certified";

  return (
    <section className="card">
      <h2>액체 사이징</h2>
      <p className="hint">
        API 520 Part I 10판 5.8 · 5.9절. 점도가 100 cP 를 넘으면 점도보정 Kv 를
        적용하며, 보정 결과가 선정 오리피스를 넘으면 한 단계 키워 자동으로
        다시 계산합니다(5.8.1.5). <strong>압력은 게이지압(kPag)</strong> 입니다 —
        기체·수증기 화면의 절대압과 다릅니다.
      </p>

      <div className="grid-form">
        <Field label="용량인증" unit="" help="certification">
          <select
            value={f.certification}
            onChange={(e) => set("certification")(e.target.value as Certification)}
          >
            <option value="certified">인증 밸브 (5.8, Kd 0.65)</option>
            <option value="non-certified">비인증 밸브 (5.9, Kd 0.62)</option>
          </select>
        </Field>
        <Field label="소요 배출량 Q" unit="L/min" help="flowVolume">
          <input value={f.flow} onChange={(e) => set("flow")(e.target.value)} />
        </Field>
        <Field label="비중 Gl" unit="물 기준" help="specificGravity">
          <input
            value={f.specificGravity}
            onChange={(e) => set("specificGravity")(e.target.value)}
          />
        </Field>
        <Field label="설정압 Ps" unit="kPag" help="setPressure">
          <input
            value={f.setPressure}
            onChange={(e) => set("setPressure")(e.target.value)}
          />
        </Field>
        <Field
          label="허용 과압"
          unit={isNonCertified ? "Kp 로 반영" : "%"}
          help="overpressure"
        >
          <input
            value={f.overpressure}
            disabled={isNonCertified}
            onChange={(e) => set("overpressure")(e.target.value)}
          />
        </Field>
        <Field label="총 배압 P2" unit="kPag" help="backpressureGauge">
          <input
            value={f.backpressure}
            onChange={(e) => set("backpressure")(e.target.value)}
          />
        </Field>
        <Field label="밸브 형식" unit="" help="valveType">
          <select
            value={f.valveType}
            onChange={(e) => set("valveType")(e.target.value as ValveType)}
          >
            {(Object.keys(VALVE_LABELS) as ValveType[]).map((t) => (
              <option key={t} value={t}>
                {VALVE_LABELS[t]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="점도 단위" unit="" help="viscosityKind">
          <select
            value={f.viscosityKind}
            onChange={(e) => set("viscosityKind")(e.target.value as ViscosityKind)}
          >
            <option value="none">점도보정 안 함</option>
            <option value="cP">절대점도 (cP)</option>
            <option value="SSU">Saybolt (SSU)</option>
          </select>
        </Field>
        <Field
          label="점도"
          unit={f.viscosityKind === "none" ? "해당 없음" : f.viscosityKind}
          help="viscosityValue"
        >
          <input
            value={f.viscosity}
            disabled={f.viscosityKind === "none"}
            onChange={(e) => set("viscosity")(e.target.value)}
          />
        </Field>
        <Field
          label="배압 보정 Kw"
          unit={isBellows ? "Figure 32 / 제작사" : "해당 없음"}
          help="kwBellows"
        >
          <input
            value={f.kw}
            disabled={!isBellows}
            placeholder={isBellows ? "예: 0.97" : "1.0 고정"}
            onChange={(e) => set("kw")(e.target.value)}
          />
        </Field>
        <Field
          label="과압 보정 Kp"
          unit={isNonCertified ? "10 % 과압이면 0.6" : "해당 없음"}
          help="kpNonCertified"
        >
          <input
            value={f.kp}
            disabled={!isNonCertified}
            placeholder={isNonCertified ? "25 % 과압이면 1.0" : "해당 없음"}
            onChange={(e) => set("kp")(e.target.value)}
          />
        </Field>
        <Field label="파열판 조합 Kc" unit="없으면 1.0" help="kc">
          <input value={f.kc} onChange={(e) => set("kc")(e.target.value)} />
        </Field>
      </div>

      {!sizing.ok ? (
        <div className="result result--error" role="alert">
          {sizing.message}
        </div>
      ) : (
        <div className="result result--ok">
          <div className="result__headline">
            <span className="result__label">소요 면적</span>
            <strong className="result__value">
              {sizing.requiredAreaMm2.toLocaleString("ko-KR", {
                maximumFractionDigits: 0,
              })}
            </strong>
            <span className="result__label">mm²</span>
            {sizing.intermediates.viscosityIterations > 0 && (
              <span className="badge">
                점도보정 {sizing.intermediates.viscosityIterations}회 반복
              </span>
            )}
          </div>

          {selection?.ok && (
            <div className="result__headline" style={{ marginTop: 10 }}>
              <span className="result__label">선정 오리피스</span>
              <strong className="result__value">
                {selection.orifice.designation}
              </strong>
              {selection.valveCount > 1 && (
                <span className="badge">{selection.valveCount}개 병렬</span>
              )}
              <span className="muted">
                여유율 {selection.marginPct.toFixed(1)} %
              </span>
            </div>
          )}
          {selection && !selection.ok && (
            <p className="muted" style={{ marginTop: 10 }}>
              {selection.message}
            </p>
          )}

          <h3>중간값 (계산서 기재용)</h3>
          <dl className="result__grid">
            <Item label="적용 식" value={sizing.equation} />
            <Item
              label="점도보정 전 면적 AR"
              value={`${sizing.intermediates.uncorrectedAreaMm2.toLocaleString("ko-KR", { maximumFractionDigits: 0 })} mm²`}
            />
            <Item
              label={
                sizing.equation === "API 520 Part I (43)"
                  ? "차압 1.25·Ps − P2"
                  : "차압 P1 − P2"
              }
              value={`${sizing.intermediates.differentialPressureKpa.toLocaleString("ko-KR", { maximumFractionDigits: 1 })} kPa`}
            />
            {sizing.intermediates.reynolds !== null && (
              <Item
                label={`Re (${sizing.intermediates.reynoldsEquation?.replace("API 520 Part I ", "식 ")})`}
                value={sizing.intermediates.reynolds.toLocaleString("ko-KR", {
                  maximumFractionDigits: 0,
                })}
              />
            )}
            {sizing.intermediates.selectedOrificeForReynolds && (
              <Item
                label="Re 계산 기준 오리피스"
                value={`${sizing.intermediates.selectedOrificeForReynolds.designation} (${sizing.intermediates.selectedOrificeForReynolds.effectiveAreaMm2.toLocaleString("ko-KR", { maximumFractionDigits: 0 })} mm²)`}
              />
            )}
            <Item label="Kv (식 34)" value={sizing.intermediates.kv.toFixed(4)} />
            <Item label="Kd" value={sizing.intermediates.kd.toFixed(3)} />
            <Item label="Kw" value={sizing.intermediates.kw.toFixed(3)} />
            <Item label="Kc" value={sizing.intermediates.kc.toFixed(3)} />
            {sizing.intermediates.kp !== null && (
              <Item label="Kp" value={sizing.intermediates.kp.toFixed(3)} />
            )}
          </dl>

          {sizing.warnings.length > 0 && (
            <ul className="warnings">
              {sizing.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
